/**
 * DriveZone — Forgot Password Page
 * Route: /forgot-password
 *
 * Step 1: enter email + first name → verified against account
 * Step 2: enter new password + confirm → reset complete
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react'
import api from '@/lib/api'
import { AxiosError } from 'axios'

type Step = 'identity' | 'password' | 'done'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('identity')

  // Step 1
  const [email, setEmail]         = useState('')
  const [firstName, setFirstName] = useState('')

  // Step 2
  const [newPassword, setNewPassword]         = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPw, setShowPw]                   = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')

  const passwordStrong = newPassword.length >= 8 && /\d/.test(newPassword)
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0

  async function handleIdentitySubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await api.post('/api/auth/forgot-password/', {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
      })
      setStep('password')
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>
      setError(axiosErr.response?.data?.message ?? 'No account found matching that name and email.')
    } finally {
      setLoading(false)
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')

    if (!passwordStrong) {
      setError('Password must be at least 8 characters and contain a number.')
      return
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await api.post('/api/auth/forgot-password/', {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        new_password: newPassword,
        confirm_password: confirmPassword,
      })
      setStep('done')
      setTimeout(() => navigate('/login', { state: { message: 'Password reset. Please sign in.' } }), 2200)
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>
      setError(axiosErr.response?.data?.message ?? 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center space-y-4 w-full max-w-sm">
          <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto" />
          <h2 className="text-xl font-semibold text-slate-900">Password updated!</h2>
          <p className="text-sm text-slate-500">Redirecting you to sign in…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="text-center mb-8">
          <img src="/favicon.svg" alt="DriveZone" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-white">DriveZone</h1>
          <p className="text-xs font-medium tracking-widest text-sky-100 uppercase mt-2">
            {step === 'identity' ? 'Verify your identity' : 'Choose a new password'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* ── Step 1: identity ── */}
          {step === 'identity' && (
            <form onSubmit={handleIdentitySubmit} className="space-y-5">
              <p className="text-sm text-slate-500">
                Enter the first name and email address on your account.
              </p>

              {/* First name */}
              <div className="relative">
                <input
                  id="fp-firstname"
                  type="text"
                  placeholder=" "
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoFocus
                  className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                             text-slate-900 text-sm
                             focus:outline-none focus:border-sky-500 focus:bg-white
                             transition-colors duration-150"
                />
                <label
                  htmlFor="fp-firstname"
                  className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                             transition-all duration-150
                             peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                             peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
                >
                  First name
                </label>
              </div>

              {/* Email */}
              <div className="relative">
                <input
                  id="fp-email"
                  type="email"
                  placeholder=" "
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                             text-slate-900 text-sm
                             focus:outline-none focus:border-sky-500 focus:bg-white
                             transition-colors duration-150"
                />
                <label
                  htmlFor="fp-email"
                  className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                             transition-all duration-150
                             peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                             peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
                >
                  E-mail
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !firstName || !email}
                className="w-full py-3.5 rounded-lg font-semibold text-sm text-white
                           bg-sky-600 hover:bg-sky-700 active:bg-sky-800
                           transition-colors duration-150
                           disabled:opacity-50 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                ) : (
                  'Continue'
                )}
              </button>
            </form>
          )}

          {/* ── Step 2: new password ── */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <p className="text-sm text-slate-500">
                Identity verified. Choose a new password for{' '}
                <span className="font-medium text-slate-700">{email}</span>.
              </p>

              {/* New password */}
              <div className="relative">
                <input
                  id="fp-newpw"
                  type={showPw ? 'text' : 'password'}
                  placeholder=" "
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  className="peer w-full px-4 pt-6 pb-2 pr-12 rounded-lg border-2 border-slate-400 bg-slate-50
                             text-slate-900 text-sm
                             focus:outline-none focus:border-sky-500 focus:bg-white
                             transition-colors duration-150"
                />
                <label
                  htmlFor="fp-newpw"
                  className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                             transition-all duration-150
                             peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                             peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
                >
                  New password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {newPassword && (
                  <p className={`text-xs mt-1.5 ${passwordStrong ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {passwordStrong ? '✓ Password looks good' : 'At least 8 characters + 1 number'}
                  </p>
                )}
              </div>

              {/* Confirm */}
              <div className="relative">
                <input
                  id="fp-confirm"
                  type={showPw ? 'text' : 'password'}
                  placeholder=" "
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="peer w-full px-4 pt-6 pb-2 rounded-lg border-2 border-slate-400 bg-slate-50
                             text-slate-900 text-sm
                             focus:outline-none focus:border-sky-500 focus:bg-white
                             transition-colors duration-150"
                />
                <label
                  htmlFor="fp-confirm"
                  className="absolute left-4 top-4 text-sm text-slate-500 pointer-events-none
                             transition-all duration-150
                             peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:text-sky-600 peer-focus:font-medium
                             peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-medium"
                >
                  Confirm password
                </label>
                {confirmPassword && (
                  <p className={`text-xs mt-1.5 ${passwordsMatch ? 'text-emerald-600' : 'text-red-500'}`}>
                    {passwordsMatch ? '✓ Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !passwordStrong || !passwordsMatch}
                className="w-full py-3.5 rounded-lg font-semibold text-sm text-white
                           bg-sky-600 hover:bg-sky-700 active:bg-sky-800
                           transition-colors duration-150
                           disabled:opacity-50 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-sky-100 mt-6">
          Remember it?{' '}
          <Link to="/login" className="text-white font-semibold hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
